import { useEffect, useMemo, useRef, useState } from 'react';
import { CustomerHero } from '@/components/CustomerHero';
import { NetworkBadge, detectNetwork } from '@/components/NetworkBadge';
import { SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { customerRecharge, tahweel } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Button, Empty, ErrorBox, Loading, Modal, Money } from '@/components/ui';
import { str, uuid } from '@/utils/format';

function kindLabel(kind: string): string {
  const map: Record<string, string> = { units: 'وحدات', bills: 'فاتورة', cash: 'كاش' };
  return map[kind] ?? kind;
}

function packagePrice(pkg: JsonMap | null, amountRaw: string): number {
  if (!pkg) return 0;
  const mode =
    str(pkg.pricing_mode) ||
    (str(pkg.operation_kind, 'units') === 'units' ? 'fixed' : 'variable');
  if (mode === 'fixed') return Number(pkg.sale_price || 0);
  const amount = Number(amountRaw || 0);
  return amount > 0 ? amount * Number(pkg.sale_rate || 0) : 0;
}

export function HomeScreen() {
  const state = useAsync(
    async () => {
      const [packagesRes, customersRes] = await Promise.all([
        customerRecharge.packages(),
        tahweel.customers(),
      ]);
      if (!packagesRes.success) return packagesRes;
      return {
        success: true,
        packages: pickList(packagesRes, 'packages', 'items', 'data'),
        customers: customersRes.success
          ? pickList(customersRes, 'customers', 'items', 'data')
          : [],
      };
    },
    (p) => ({
      packages: Array.isArray(p.packages) ? (p.packages as JsonMap[]) : pickList(p, 'packages'),
      customers: Array.isArray(p.customers) ? (p.customers as JsonMap[]) : pickList(p, 'customers'),
    }),
    [],
  );

  const packages = state.data?.packages ?? [];
  const customers = state.data?.customers ?? [];

  const [customerId, setCustomerId] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('units');
  const [packageId, setPackageId] = useState('');
  const [amount, setAmount] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const idemRef = useRef('');

  const network = useMemo(() => detectNetwork(phone), [phone]);
  const digits = phone.replace(/\D/g, '');
  const phoneStarted = digits.length >= 3;

  const services = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; pricing_mode: string; sort: number }>();
    packages
      .filter((p) => !network || str(p.company, 'syriatel') === network)
      .forEach((p, idx) => {
        const slug = str(p.operation_kind, 'units');
        if (!map.has(slug)) {
          map.set(slug, {
            slug,
            name: str(p.service_name, kindLabel(slug)),
            pricing_mode: str(p.pricing_mode) === 'variable' ? 'variable' : 'fixed',
            sort: idx,
          });
        }
      });
    return [...map.values()];
  }, [packages, network]);

  useEffect(() => {
    const first = services[0];
    if (first && !services.some((s) => s.slug === service)) {
      setService(first.slug);
    }
  }, [services, service]);

  useEffect(() => {
    setPackageId('');
    setAmount('');
    setPickerOpen(false);
  }, [network, service]);

  const currentService = services.find((s) => s.slug === service);
  const fixedMode = currentService?.pricing_mode !== 'variable';

  const filtered = useMemo(() => {
    if (!network) return [];
    return packages.filter(
      (p) => str(p.company, 'syriatel') === network && str(p.operation_kind, 'units') === service,
    );
  }, [packages, network, service]);

  const selected = useMemo(() => {
    if (!network) return null;
    if (fixedMode) {
      return filtered.find((p) => String(p.id) === packageId) || filtered[0] || null;
    }
    const value = Number(amount || 0);
    return (
      filtered.find(
        (p) => value >= Number(p.min_amount || 0) && value <= Number(p.max_amount || 0),
      ) ||
      filtered[0] ||
      null
    );
  }, [filtered, packageId, fixedMode, amount, network]);

  const total = packagePrice(selected, amount);

  function pickCustomer(id: string) {
    setCustomerId(id);
    const c = customers.find((x) => String(x.id) === id);
    if (c) setPhone(str(c.phone));
  }

  async function saveCustomer() {
    const t = newPhone.replace(/\D/g, '');
    if (!newName.trim()) {
      setMsg('اكتب اسم الزبون.');
      return;
    }
    if (!/^09\d{8}$/.test(t)) {
      setMsg('رقم الزبون غير صالح.');
      return;
    }
    setBusy(true);
    const res = await tahweel.saveCustomer({ name: newName.trim(), phone: t });
    setBusy(false);
    setMsg(res.success ? 'تم حفظ الزبون.' : errorMessage(res));
    if (res.success) {
      setSaveOpen(false);
      setNewName('');
      setNewPhone('');
      state.reload();
      setPhone(t);
    }
  }

  async function placeOrder() {
    if (busy) return;
    const n = phone.replace(/\D/g, '');
    if (!/^09\d{8}$/.test(n)) {
      setMsg('أدخل رقم هاتف صحيح.');
      return;
    }
    const net = detectNetwork(n);
    if (!net) {
      setMsg('هذا الرقم غير تابع لسيريتل أو MTN حسب البادئة.');
      return;
    }
    if (!selected) {
      setMsg(fixedMode ? 'اختر الفئة أولاً.' : 'لا توجد تسعيرة مناسبة لهذه القيمة.');
      return;
    }
    if (str(selected.company) !== net) {
      setMsg('الفئة لا تطابق شبكة الرقم.');
      return;
    }
    const body: JsonMap = {
      package_id: selected.id,
      target_phone: n,
      ...(customerId ? { customer_id: Number(customerId) } : {}),
    };
    if (!fixedMode) {
      const value = Number(amount || 0);
      if (value <= 0) {
        setMsg('أدخل قيمة صحيحة.');
        return;
      }
      if (value < Number(selected.min_amount || 0) || value > Number(selected.max_amount || 0)) {
        setMsg('القيمة خارج حدود التسعيرة المتاحة.');
        return;
      }
      body.amount = value;
    }
    if (!idemRef.current) idemRef.current = uuid();
    const idempotencyKey = idemRef.current;
    setBusy(true);
    setMsg('');
    const res = await customerRecharge.order(body, idempotencyKey);
    setBusy(false);
    if (res.success && res.idempotent_replay) {
      // Same click/replay — show prior result, do not pretend a brand-new order was created.
      setMsg(str(res.message, 'تم استلام هذا الطلب مسبقاً.'));
      idemRef.current = '';
      state.reload();
      return;
    }
    setMsg(res.success ? str(res.message, 'تم إرسال الطلب بنجاح.') : errorMessage(res));
    if (res.success) {
      idemRef.current = '';
      setPhone('');
      setCustomerId('');
      setAmount('');
      setPackageId('');
      state.reload();
    } else {
      // Allow retry with a fresh key after a hard failure.
      idemRef.current = '';
    }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <div className="customer-page customer-home-page">
      <CustomerHero
        placement="units"
        fallbackTitle="اشحنلي وحدات"
        fallbackText="اشحن الرصيد والوحدات بسهولة وأمان وفي أي وقت."
        icon="wallet"
      />

      <SectionCard
        title="طلب خدمة رصيد"
        subtitle="اكتب الرقم أو اختر زبوناً محفوظاً، ثم اختر الخدمة والفئة."
        icon={<Icon name="deposit" size={24} />}
        className="home-recharge recharge-request-card"
      >
        <div className="request-flow compact-request-flow">
          <div className="saved-customer-selector">
            <label className="field">
              <span>اختيار زبون محفوظ (اختياري)</span>
              <select value={customerId} onChange={(e) => pickCustomer(e.target.value)}>
                <option value="">إدخال رقم جديد يدوياً</option>
                {customers.map((c) => (
                  <option key={str(c.id)} value={str(c.id)}>
                    {str(c.name)} — {str(c.phone)}
                  </option>
                ))}
              </select>
            </label>
            <Button type="button" variant="secondary" onClick={() => setSaveOpen(true)}>
              <Icon name="users" size={17} /> حفظ زبون جديد
            </Button>
          </div>

          <label className="field phone-first-field network-phone-field">
            <span>رقم الهاتف</span>
            <div
              className={`network-input-shell ${network || ''} ${phoneStarted && !network ? 'invalid' : ''}`}
            >
              <NetworkBadge company={network} phone={phone} />
              <input
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setCustomerId('');
                }}
                inputMode="tel"
                placeholder="09xxxxxxxx"
              />
            </div>
          </label>

          <div className="choice-row dynamic-service-tabs">
            {services.map((s) => (
              <button
                key={s.slug}
                type="button"
                className={service === s.slug ? 'active' : ''}
                onClick={() => setService(s.slug)}
              >
                {s.name}
              </button>
            ))}
          </div>

          {network ? (
            filtered.length === 0 ? (
              <Empty title="لا توجد فئات متاحة" hint="الخدمة متوقفة أو لا توجد تسعيرة مفعلة حالياً." />
            ) : (
              <>
                {fixedMode ? (
                  <div className="field">
                    <span>الفئة</span>
                    <div className="package-picker">
                      <button
                        type="button"
                        className={`package-picker-trigger ${pickerOpen ? 'open' : ''}`}
                        onClick={() => setPickerOpen(true)}
                      >
                        <div className="package-picker-meta">
                          <strong>{str(selected?.label, 'اختر الفئة المناسبة')}</strong>
                          <small>
                            {selected ? (
                              <Money value={selected.sale_price} />
                            ) : (
                              'اضغط لعرض الفئات والأسعار'
                            )}
                          </small>
                        </div>
                        <span className="package-picker-arrow">▾</span>
                      </button>
                    </div>
                    {pickerOpen ? (
                      <div className="package-modal-backdrop" onClick={() => setPickerOpen(false)}>
                        <div className="package-modal" onClick={(e) => e.stopPropagation()}>
                          <div className="package-modal-head">
                            <div>
                              <h3>اختر الفئة</h3>
                              <p>
                                {currentService?.name || 'الخدمة'} — اختر الفئة المناسبة ثم تابع
                                التنفيذ.
                              </p>
                            </div>
                            <button
                              type="button"
                              className="package-modal-close"
                              onClick={() => setPickerOpen(false)}
                            >
                              ×
                            </button>
                          </div>
                          <div className="package-modal-grid">
                            {filtered.map((pkg) => (
                              <button
                                key={str(pkg.id)}
                                type="button"
                                className={`package-modal-card ${
                                  String(selected?.id || '') === String(pkg.id) ? 'selected' : ''
                                }`}
                                onClick={() => {
                                  setPackageId(String(pkg.id));
                                  setPickerOpen(false);
                                }}
                              >
                                <strong>{str(pkg.label)}</strong>
                                <span>
                                  <Money value={pkg.sale_price} />
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <label className="field">
                    <span>قيمة {currentService?.name || 'الخدمة'}</span>
                    <input
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      inputMode="decimal"
                      placeholder="أدخل القيمة"
                    />
                  </label>
                )}

                <div className="order-summary checkout-summary">
                  <span>المبلغ المطلوب خصمه من محفظة الوحدات</span>
                  <strong>
                    <Money value={total} />
                  </strong>
                </div>
                <button
                  type="button"
                  className="btn btn-primary big-action checkout-btn"
                  disabled={busy || !selected || total <= 0}
                  onClick={() => void placeOrder()}
                >
                  {busy ? 'جاري التنفيذ...' : 'تنفيذ'}
                </button>
              </>
            )
          ) : (
            <div className="network-wait-card">
              <span>{phoneStarted ? 'الرقم غير مدعوم حالياً' : 'أدخل رقم الهاتف للمتابعة'}</span>
            </div>
          )}

          {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}
        </div>
      </SectionCard>

      <Modal open={saveOpen} title="حفظ زبون سريع" onClose={() => setSaveOpen(false)}>
        <div className="form-grid">
          <label>
            <span>الاسم</span>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} />
          </label>
          <label>
            <span>رقم الهاتف</span>
            <input
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              inputMode="tel"
              placeholder="09xxxxxxxx"
            />
          </label>
          <Button type="button" disabled={busy} onClick={() => void saveCustomer()}>
            حفظ
          </Button>
        </div>
      </Modal>
    </div>
  );
}
